import React, { useEffect, useState } from "react";

//UI Material
import { CircularProgress, Divider, Grid, TextField } from "@mui/material";

//Local components
import ErrorBox from "../Components/ErrorBox";
import RegisteredProductsList from "../Components/assembly/RegisteredProductsList";
import ColaboratorsList from "../Components/assembly/ColaboratorsList";
import RegisterAssembly from "../Components/assembly/RegisterAssembly";
import FixedCollaborator from "../Components/assembly/FixedCollaborator";
import ProductivitySummary from "../Components/assembly/ProductivitySummary";
import ResultsGraph from "../Components/assembly/ResultsGraph";
import DataLoader from "../Components/basics/DataLoader";

//Helpers
import { typographyForMainTitles } from "../Helpers/typography";

//X UI Material
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { fetchWithTokenJSONPost } from "../Helpers/network";

let EXPECTED_DAILY_PRODUCTION = 20;

export default function Assembly() {
  // ==========> Component States<==========
  //State for loading
  const [isLoading, setIsLoading] = useState(true);
  //State for setting error when could not get data
  const [dataError, setDataError] = useState(false);
  //State for selected date
  const [selectedDate, setSelectedDate] = useState(new Date());
  //State for data
  const [registersList, setRegistersList] = useState([]);
  //State for users list
  const [usersList, setUsersList] = useState([]);
  //State for products list
  const [productsList, setProductsList] = useState([]);

  // ==========> Component Functions <==========
  //Function to get registers list
  const getData = async () => {
    try {
      // Prepare the requests but do not await them here
      const registersRequest = fetchWithTokenJSONPost(`/assembly/get-all`, {
        howMany: 5000,
      });
      const usersRequest = fetchWithTokenJSONPost(`/users/get`, {
        howMany: 500,
      });

      // Use Promise.all to await both requests simultaneously
      const [registersResponse, usersResponse] = await Promise.all([
        registersRequest,
        usersRequest,
      ]);

      // Process registers response
      if (registersResponse.status === 200) {
        const jsonResponse = await registersResponse.json();
        setRegistersList(jsonResponse.data);
      } else {
        if (registersResponse.status === 401) {
          window.location.href = "/";
        }
      }

      // Process users response
      if (usersResponse.status === 200) {
        const jsonResponse = await usersResponse.json();
        setUsersList(jsonResponse.data);
      } else {
        if (usersResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(true);
    }
    setIsLoading(false);
  };

  //Function to get products list
  const getProductsList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/products/get-products-allowed-for-assembly`
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setProductsList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(true);
    }
  };

  // ==========>Component UseEffects<==========
  useEffect(() => {
    getData();
    getProductsList();
  }, []);

  if (isLoading) {
    return <DataLoader />;
  }

  // ==========> Component <==========
  return dataError ? (
    <ErrorBox />
  ) : (
    <Grid container spacing={1} textAlign="center">
      <Grid item xs={12}>
        {typographyForMainTitles("Producción")}
      </Grid>
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MobileDatePicker
            label="Fecha de consulta"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            renderInput={(params) => (
              <TextField {...params} variant="standard" />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={12} md={4}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <ProductivitySummary
            data={registersList}
            selectedDate={selectedDate}
            expectation={EXPECTED_DAILY_PRODUCTION}
          />
        )}
      </Grid>
      <Grid item xs={12} md={8}>
        <Grid container justifyContent="flex-start" spacing={1}>
          {usersList
            .filter((u) => u.fixedToAssembly)
            .map((u) => (
              <Grid item xs={6} sm={6} md={4} lg={3} key={u.user}>
                <FixedCollaborator
                  data={registersList}
                  selectedDate={selectedDate}
                  user={u}
                  expectation={EXPECTED_DAILY_PRODUCTION / 2}
                />
              </Grid>
            ))}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} sm={5} md={3} lg={2}>
        <RegisterAssembly getData={getData} products={productsList} />
      </Grid>
      <Grid item xs={12} sm={7} md={9} lg={10}>
        <RegisteredProductsList
          data={registersList}
          getData={getData}
          users={usersList}
        />
      </Grid>
      <Grid item xs={12} sx={{ marginTop: "20px" }}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        {typographyForMainTitles("Historial de resultados")}
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <ResultsGraph
          data={registersList.filter(
            (d) =>
              new Date().getFullYear() === new Date(d.assemblyAt).getFullYear()
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <ResultsGraph
          data={registersList.filter(
            (d) =>
              new Date().getFullYear() === new Date(d.assemblyAt).getFullYear()
          )}
          type={"tested"}
        />
      </Grid>

      <Grid item xs={12}>
        <ColaboratorsList
          data={registersList}
          users={usersList.filter((u) => u.position === "Equipo de Producción")}
        />
      </Grid>
    </Grid>
  );
}
